import React, { useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion } from 'framer-motion';
import { MessageSquare, Users, Clock, Check, CheckCheck, AlertCircle } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { getMemberByUser } from '@/lib/memberLookup';

export default function Mensagens() {
  const queryClient = useQueryClient();

  const { data: currentUser, isLoading: loadingUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const { data: memberData, isLoading: loadingMember } = useQuery({
    queryKey: ['memberData', currentUser?.id, currentUser?.email],
    queryFn: () => getMemberByUser(currentUser),
    enabled: !!currentUser
  });
  const activeMember = memberData?.status === 'Ativo' ? memberData : null;

  const { data: messages = [], isLoading: loadingMessages } = useQuery({
    queryKey: ['messages'],
    queryFn: () => base44.entities.Message.list('-created_date', 50),
    enabled: !!activeMember
  });

  const markAsReadMutation = useMutation({
    mutationFn: async ({ messageId, readBy }) => {
      const updatedReadBy = [...(readBy || [])];
      if (currentUser && !updatedReadBy.includes(currentUser.id)) {
        updatedReadBy.push(currentUser.id);
      }
      return base44.entities.Message.update(messageId, { read_by: updatedReadBy });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['messages'] });
    }
  });

  // Mark messages as read when viewing
  useEffect(() => {
    if (currentUser && messages.length > 0) {
      messages.forEach(message => {
        if (!message.read_by?.includes(currentUser.id)) {
          markAsReadMutation.mutate({ messageId: message.id, readBy: message.read_by });
        }
      });
    }
  }, [currentUser, messages]);

  const audienceLabels = {
    'Todos': 'Para todos os membros',
    'Ministério': 'Para ministério específico',
    'Grupo': 'Para grupo específico'
  };

  if (loadingUser || loadingMember || (activeMember && loadingMessages) || (!currentUser && loadingUser)) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  if (!activeMember) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white pt-8 pb-6 px-4">
          <div className="max-w-lg mx-auto">
            <PageHeader title="Comunicados" subtitle="Mensagens da liderança" showBack={true} />
          </div>
        </div>
        <div className="max-w-lg mx-auto px-4 py-12">
          <div className="bg-white rounded-xl p-8 shadow-sm text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Acesso Restrito</h3>
            <p className="text-gray-600">Apenas membros ativos podem acessar os comunicados.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader title="Comunicados" subtitle="Mensagens da liderança" showBack={true} />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {messages.length === 0 ? (
          <EmptyState
            icon={MessageSquare}
            title="Nenhum comunicado"
            description="Não há mensagens no momento."
          />
        ) : (
          <div className="space-y-4">
            {messages.map((message, index) => {
              const isRead = currentUser && message.read_by?.includes(currentUser.id);
              
              return (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`bg-white rounded-2xl shadow-sm p-4 ${!isRead ? 'border-l-4 border-indigo-500' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{message.title}</h3>
                        {!isRead && (
                          <Badge className="bg-indigo-100 text-indigo-700 text-xs">Novo</Badge>
                        )}
                      </div>
                      <p className="text-gray-600 mt-2 whitespace-pre-line">{message.content}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-3 border-t">
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {format(new Date(message.created_date), "d 'de' MMM", { locale: ptBR })}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {message.audience_name || audienceLabels[message.audience_type]}
                      </span>
                    </div>
                    {isRead ? (
                      <CheckCheck className="w-5 h-5 text-indigo-500" />
                    ) : (
                      <Check className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
